"use client";

import Link from "next/link";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { Copy, Download, ExternalLink, Trash2, X } from "lucide-react";
import { Button } from "@repo/ui";
import { Badge } from "@repo/ui";
import { cn } from "@/lib/utils";
import {
  isPermanentMediaUrl,
  PyloMediaLightbox,
  toDownloadUrl,
} from "@/components/pylo-media-lightbox";
import { toast } from "sonner";
import {
  getPyloMediaUsage,
  type MediaUsageRelation,
  type PyloMedia,
} from "@/services/pylo-media.service";
import { formatFileSize } from "./media-utils";

interface MediaInspectorProps {
  media: PyloMedia;
  relations: Array<MediaUsageRelation>;
  /** Overlays the results area instead of taking a column — for narrow layouts. */
  overlay?: boolean;
  onClose?: () => void;
  onDelete?: (media: PyloMedia) => void;
}

const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex items-start justify-between gap-3 py-1">
    <span className="shrink-0 text-[12px] text-muted-foreground">{label}</span>
    <span className="min-w-0 break-words text-right text-[12px]">{value}</span>
  </div>
);

export const MediaInspector = ({
  media,
  relations,
  overlay = false,
  onClose,
  onDelete,
}: MediaInspectorProps) => {
  const usage = useQuery({
    queryKey: ["media-usage", media.id, relations.map((relation) => relation.fieldName)],
    queryFn: () => getPyloMediaUsage(media.id, relations),
    enabled: relations.length > 0,
  });

  const createdAt = media.created_at ? new Date(media.created_at) : null;
  const entries = usage.data ?? [];

  return (
    <aside
      className={cn(
        "flex w-80 shrink-0 flex-col overflow-y-auto border-l bg-white",
        overlay && "absolute inset-y-0 right-0 z-20 shadow-lg",
      )}
    >
      <div className="relative flex items-center justify-center border-b bg-gray-50 p-4">
        {onClose && (
          <Button
            variant="ghost"
            size="sm"
            className="absolute right-1.5 top-1.5 h-7 w-7 bg-white/80 p-0 hover:bg-white"
            onClick={onClose}
            aria-label="Close details"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}

        <PyloMediaLightbox
          url={media.url}
          mediaId={media.id}
          fileName={media.file_name}
          mimeType={media.file_mime_type}
          thumbnailSize="lg"
          showPdfPreview
        />
      </div>

      <div className="flex flex-col gap-1 border-b px-4 py-3">
        <h2 className="break-words text-[14px] font-semibold" title={media.file_name}>
          {media.file_name}
        </h2>
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant="outline" className="text-[11px]">
            {media.file_mime_type ?? "unknown"}
          </Badge>
          {media.is_public && (
            <Badge variant="outline" className="text-[11px]">
              Public
            </Badge>
          )}
        </div>
      </div>

      <div className="flex flex-col border-b px-4 py-3">
        <Row label="Size" value={formatFileSize(media.file_size_bytes)} />
        <Row
          label="Uploaded"
          value={createdAt && !Number.isNaN(createdAt.getTime()) ? format(createdAt, "PPp") : "—"}
        />
        <Row label="ID" value={<span className="font-mono text-[11px]">{media.id}</span>} />
      </div>

      <div className="flex flex-col gap-2 border-b px-4 py-3">
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Used by {entries.length > 0 && `(${entries.length})`}
        </h3>

        {relations.length === 0 ? (
          <p className="text-[12px] leading-snug text-muted-foreground">
            No entity in this workspace links to files, so usage cannot be resolved.
          </p>
        ) : usage.isLoading ? (
          <p className="text-[12px] text-muted-foreground">Loading…</p>
        ) : entries.length === 0 ? (
          <p className="text-[12px] text-muted-foreground">Not referenced by any record.</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {entries.map((entry) => (
              <li key={`${entry.relation.fieldName}-${entry.instanceId}`}>
                <Link
                  href={`/${entry.relation.entityName}`}
                  className="flex items-center gap-2 rounded-md px-2 py-1 text-[12px] hover:bg-gray-50"
                >
                  <span className="shrink-0 text-muted-foreground">
                    {entry.relation.entityName}
                  </span>
                  <span className="min-w-0 flex-1 truncate font-medium">{entry.label}</span>
                  <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex flex-col gap-2 px-4 py-3">
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="h-8 flex-1 gap-1.5 text-[13px]" asChild>
            <a href={toDownloadUrl(media.url)} target="_blank" rel="noopener noreferrer">
              <Download className="h-3.5 w-3.5" />
              Download
            </a>
          </Button>
          {isPermanentMediaUrl(media.url) && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 text-[13px]"
              onClick={() => {
                navigator.clipboard.writeText(media.url);
                toast.success("Link copied");
              }}
              title="Copy link"
            >
              <Copy className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
        {onDelete && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 gap-1.5 text-[13px] text-destructive hover:bg-destructive/10 hover:text-destructive"
            onClick={() => onDelete(media)}
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete file
          </Button>
        )}
      </div>
    </aside>
  );
};
